import { Icon, IconButton, Theme, SxProps } from "@outschool/backpack";
import { Search as SearchFilters } from "@outschool/business-rules";
import { SearchFilters as SearchFiltersType } from "@outschool/gql-backend-generated";
import {
  DeleteSavedSearchMutation,
  DeleteSavedSearchMutationVariables,
  SaveSearchMutationMutation,
  SaveSearchMutationMutationVariables,
} from "@outschool/gql-frontend-generated";
import { faBookmark, fasBookmark } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useTrackEvent } from "@outschool/ui-analytics";
import { Mutation, MutationFunction } from "@outschool/ui-apollo";
import {
  AuthTrigger,
  AuthTriggerActionTypes,
  useLoginFlowContext,
  useSession,
} from "@outschool/ui-auth";
import { ToggleButton } from "@outschool/ui-legacy-component-library";
import React from "react";

import {
  deleteSavedSearchMutation,
  deleteSavedSearchOptimisticResponse,
  deleteSavedSearchUpdate,
  saveSearchMutation,
  saveSearchOptimisticResponse,
  saveSearchUpdate,
} from "./graphql";
import { useSavedSearches } from "./hooks";
import {
  addParamsToUrl,
  onboardingQuizPath,
  pathFromLocation,
} from "@outschool/routes";

const smallVariantSx: SxProps = {
  color: "#6161F2",
  borderColor: "#6161F2 !important",
  padding: "9px",
  backgroundColor: "white !important",
  "&:disabled": {
    borderColor: "grey",
  },
};

const airlineVariantSx = (theme: Theme): SxProps => ({
  color: "#454548",
  fontSize: 18,
  "&:hover:not(:disabled),&:focus:not(:disabled)": {
    backgroundColor: theme.palette.overlays.transparentGrayLight,
  },
  "&:active:not(:disabled)": {
    backgroundColor: theme.palette.overlays.transparentGrayDark,
  },
});

const filterFilters = (filters?: Record<string, any>) =>
  filters
    ? Object.fromEntries(
        Object.entries(filters).filter(([_k, v]) => v != null && v !== "")
      )
    : {};

export default function SaveSearchButton({
  filters,
  variant,
}: {
  filters?: SearchFiltersType;
  variant: "small" | "large" | "airline";
}) {
  const trackEvent = useTrackEvent();
  const { isLoggedIn } = useSession();
  const { enterLoginFlow } = useLoginFlowContext();
  const { savedSearches } = useSavedSearches();
  const savedSearch = savedSearches.find(search =>
    SearchFilters.equal(filterFilters(search.filters), filterFilters(filters))
  );

  const alreadySaved = !!savedSearch;
  const { t } = useTranslation(
    "ui-components-website\\src\\Search\\SavedSearch\\SaveSearchButton"
  );

  const onSaveClick = React.useCallback(
    (
        saveSearch: MutationFunction<
          SaveSearchMutationMutation,
          SaveSearchMutationMutationVariables
        >
      ) =>
      async (e: React.MouseEvent<HTMLElement>) => {
        e.currentTarget.blur();
        trackEvent("save_search_badge_touch", {
          savedSearchAction: "save",
          isLoggedIn,
          searchFilters: JSON.stringify(filters),
        });
        if (!isLoggedIn) {
          enterLoginFlow({
            authTrigger: AuthTrigger.SAVE_SEARCH,
            postLoginAction: {
              actionType: AuthTriggerActionTypes.SAVE_SEARCH_AFTER_LOGIN,
              graphqlMutation: {
                mutation: saveSearchMutation,
                variables: { filters },
              },
              welcomeModalData: undefined,
              shouldPopUpWelcomeModal: false,
            },
            newUrlAfterSignUp: addParamsToUrl(onboardingQuizPath(), {
              savedSearch: true,
            }),
            newUrlAfterLogin: pathFromLocation(window.location),
            shouldNavigateOverwrite: false,
          });
        } else if (!alreadySaved && !!filters) {
          await saveSearch({ variables: { filters } });
        }
      },
    [alreadySaved, isLoggedIn, filters, trackEvent, enterLoginFlow]
  );

  const onDeleteClick = React.useCallback(
    (
        deleteSavedSearch: MutationFunction<
          DeleteSavedSearchMutation,
          DeleteSavedSearchMutationVariables
        >
      ) =>
      async (e: React.MouseEvent<HTMLElement>) => {
        e.currentTarget.blur();
        trackEvent("save_search_badge_touch", {
          savedSearchAction: "unsave",
          savedSearchUid: savedSearch?.uid,
          isLoggedIn,
          searchFilters: JSON.stringify(filters),
        });
        if (!!savedSearch?.uid) {
          await deleteSavedSearch({
            variables: {
              uid: savedSearch.uid,
            },
          });
        }
      },
    [isLoggedIn, filters, trackEvent, savedSearch?.uid]
  );

  if (alreadySaved) {
    return (
      <Mutation
        mutation={deleteSavedSearchMutation}
        update={deleteSavedSearchUpdate}
        optimisticResponse={deleteSavedSearchOptimisticResponse}
      >
        {(
          deleteSavedSearch: MutationFunction<
            DeleteSavedSearchMutation,
            DeleteSavedSearchMutationVariables
          >
        ) =>
          variant === "airline" ? (
            <IconButton
              icon={fasBookmark}
              size="small"
              color="neutral"
              variant="text"
              aria-label={t("Unsave Search")}
              sx={airlineVariantSx}
              onClick={onDeleteClick(deleteSavedSearch)}
            />
          ) : variant === "small" ? (
            <IconButton
              variant="outlined"
              icon={fasBookmark}
              aria-label={t("Unsave Search")}
              sx={smallVariantSx}
              onClick={onDeleteClick(deleteSavedSearch)}
            />
          ) : (
            <ToggleButton
              size="small"
              enabled={true}
              onClick={onDeleteClick(deleteSavedSearch)}
              aria-label={t("Unsave Search")}
              sx={{ height: "44px" }}
            >
              <Icon icon={fasBookmark} />
            </ToggleButton>
          )
        }
      </Mutation>
    );
  }

  return (
    <Mutation
      mutation={saveSearchMutation}
      update={saveSearchUpdate}
      optimisticResponse={saveSearchOptimisticResponse(filters)}
    >
      {(
        saveSearch: MutationFunction<
          SaveSearchMutationMutation,
          SaveSearchMutationMutationVariables
        >
      ) =>
        variant === "airline" ? (
          <IconButton
            icon={faBookmark}
            size="small"
            color="neutral"
            variant="text"
            aria-label={t("Unsave Search")}
            sx={airlineVariantSx}
            onClick={onSaveClick(saveSearch)}
          />
        ) : variant === "small" ? (
          <IconButton
            variant="outlined"
            icon={faBookmark}
            aria-label={t("Save Search")}
            sx={smallVariantSx}
            onClick={onSaveClick(saveSearch)}
          />
        ) : (
          <ToggleButton
            size="small"
            enabled={false}
            onClick={onSaveClick(saveSearch)}
            aria-label={t("Save Search")}
            sx={{ height: "44px", borderWidth: "2px" }}
          >
            <Icon icon={faBookmark} />
          </ToggleButton>
        )
      }
    </Mutation>
  );
}
